import { useCallback, useMemo, useState } from "react";
import { toast } from "sonner";
import { useTranslation } from "react-i18next";
import orderBy from "lodash/orderBy";
import { useAnalytics } from "~/contexts/analytics-context";
import { Tabs } from "~/components/ui/tabs";

import { BookDescriptionTab } from "~/pages/product-details/tabs/book-description-tab/book-description-tab";
import { ProductSummary } from "~/pages/product-details/components/product-summary/product-summary";
import { ProductDetailsAddToProject } from "~/pages/product-details/components/add-to-project";

import { useProduct } from "~/contexts/product-context";
import { useSession } from "~/contexts/auth-context";
import { useFeatureFlags } from "~/contexts/feature-flag-context";
import { useKeywordsPipelineInfoDialog } from "~/contexts/keywords-pipeline-info-dialog-context";
import { useProject } from "~/hooks/use-project";
import { Button } from "~/components/button";
import { KeywordsTab } from "~/components/keywords-tab";
import { useDialog } from "~/hooks/use-dialog";
import { getKeywordsByType } from "~/integrations/firebase/collections";
import { RegenerateKeywordsDialog } from "~/pages/product-details/tabs/keywords-tab/components/generated-keywords/regenerate-keywords-dialog";

import { CodesTab } from "~/pages/product-details/tabs/codes-tab/codes-tab";
import { MarketingSubtitlesPanel } from "~/components/marketing-subtitles-panel";
import { RawDataTab } from "~/pages/product-details/tabs/raw-data-tab/raw-data-tab";
import { SalesDataTab } from "~/pages/product-details/tabs/sales-data-tab";
import { APlusContentTab } from "~/pages/product-details/tabs/a-plus-content-tab";
import { SummaryTab } from "~/pages/product-details/tabs/summary-tab/summary-tab";
import { ProductDetailsTabBox } from "~/pages/product-details/components/product-details-tab-box/product-details-tab-box";
import { saveProductAsExcel } from "~/shared/services/export-products-service";
import { useKeywordGeneration } from "~/components/keywords-tab/hooks/use-keyword-generation";

import { logError } from "~/shared/services/error-reporting";
import { useProject as useProjectContext } from "~/contexts/project-context";
import { useSearchParams } from "~/hooks/use-search-params";
import { PRODUCT_DETAILS_TAB } from "./product-navigation-bar.types";
import { ProductNavigationBarButtons } from "./product-navigation-bar-buttons";
import { ProductNavigationBarTabNavigation } from "./product-navigation-bar-tab-navigation";

const SALES_EXPLAINER_SEEN_KEY = "reedy.sales.explainerSeen.v1";

export const ProductNavigationBarContent = () => {
  const { gaEvent } = useAnalytics();
  const { product } = useProduct();
  const { user } = useSession();
  const { features } = useFeatureFlags();
  const { t } = useTranslation(["productDetails", "general"]);
  const [searchParams, setSearchParams] = useSearchParams();
  const requestedTab = searchParams.get("tab") as PRODUCT_DETAILS_TAB | null;
  const visibleTabs = Object.values(PRODUCT_DETAILS_TAB).filter(
    (tab) =>
      (tab !== PRODUCT_DETAILS_TAB.SUMMARY || features.essenceIntegration) &&
      (tab !== PRODUCT_DETAILS_TAB.A_PLUS_CONTENT || features.showAPlusTab) &&
      (tab !== PRODUCT_DETAILS_TAB.RAW_DATA || user.isSuperAdmin),
  );
  const activeTab =
    requestedTab && visibleTabs.includes(requestedTab)
      ? requestedTab
      : PRODUCT_DETAILS_TAB.KEYWORDS;
  const { projectId } = useProjectContext();
  const { products, project } = useProject(projectId);
  const { open: openKeywordsPipelineInfoDialog } =
    useKeywordsPipelineInfoDialog();
  const {
    onOpenChange: onRegenerateDialogChange,
    isOpen: isRegenerateDialogOpen,
    onOpen: onRegenerateDialogOpen,
  } = useDialog("regenerate-keywords");

  const { generated: generatedKeywords } = getKeywordsByType(product);
  const hasGeneratedKeywords = generatedKeywords.length > 0;

  const { isGenerating: isRegenerating } = useKeywordGeneration();
  const [showReasoning, setShowReasoning] = useState(false);

  const [isSalesExplainerOpen, setIsSalesExplainerOpen] = useState(() => {
    try {
      return !localStorage.getItem(SALES_EXPLAINER_SEEN_KEY);
    } catch {
      return true;
    }
  });

  const dismissSalesExplainer = () => {
    try {
      localStorage.setItem(SALES_EXPLAINER_SEEN_KEY, "true");
    } catch {
      // Storage unavailable; dismissal just won't persist across visits.
    }
    setIsSalesExplainerOpen(false);
  };

  const [previousProduct, nextProduct] = useMemo(() => {
    if (!products?.length) {
      return [];
    }

    const ordered = orderBy(products, ({ isbn }) => isbn, "asc");
    const index = ordered.findIndex(({ id }) => id === product?.id);

    const previous = ordered[index - 1];
    const next = ordered[index + 1];

    return [previous?.id, next?.id];
  }, [products, product?.id]);

  const handleClick = (tab: PRODUCT_DETAILS_TAB) => {
    gaEvent({
      type: "click",
      payload: { analytics_id: "change_search_term_tab", text: tab },
    });
  };

  const handleShowReasoningChange = (value: boolean) => {
    setShowReasoning(value);
    gaEvent({
      type: "toggle_keyword_reasoning_switch",
      payload: {
        enabled: value,
        book: product?.id,
      },
    });
  };

  const onTabChange = (tab: string) => {
    searchParams.set("tab", tab);
    setSearchParams(searchParams);
  };

  const handleExcelExportButtonClick = useCallback(async () => {
    try {
      if (!product) {
        return;
      }
      gaEvent({ type: "export_book", payload: {} });
      await saveProductAsExcel(
        product,
        product.title || product.isbn || "untitled",
      );
    } catch (e) {
      logError(e);
      toast.error(t("productDetails:buttons.export.error"));
    }
  }, [gaEvent, product, t]);

  return (
    <Tabs value={activeTab} onValueChange={onTabChange}>
      <div className="flex flex-col items-start gap-3">
        <div className="flex flex-row items-start justify-between w-full">
          <ProductSummary
            title={product?.title ?? ""}
            authors={product?.author ?? []}
            isbn={product?.isbn ?? ""}
            productGroupDescription={product?.productGroupDescription ?? ""}
          />
          <div className="gap-4 flex flex-col">
            <ProductNavigationBarButtons
              projectId={project?.id}
              productId={product?.id}
              previousProductId={previousProduct}
              nextProductId={nextProduct}
              activeTab={activeTab}
            />
            <div className="flex justify-end gap-2">
              <ProductDetailsAddToProject />
              <Button
                variant="primary"
                label={t("productDetails:buttons.export.title")}
                icon="download"
                onClick={handleExcelExportButtonClick}
                size="small"
                analyticsId="button_export_product_xls"
              />
            </div>
          </div>
        </div>
        <ProductNavigationBarTabNavigation
          activeTab={activeTab}
          hasGeneratedKeywords={hasGeneratedKeywords}
          isRegenerating={isRegenerating}
          onTabClick={handleClick}
          onPipelineInfoOpen={openKeywordsPipelineInfoDialog}
          onRegenerateOpen={onRegenerateDialogOpen}
          showReasoning={showReasoning}
          onShowReasoningChange={handleShowReasoningChange}
          isSalesExplainerOpen={isSalesExplainerOpen}
          onSalesExplainerOpen={() => setIsSalesExplainerOpen(true)}
        />
      </div>

      {features.essenceIntegration && (
        <ProductDetailsTabBox value="summary">
          <SummaryTab />
        </ProductDetailsTabBox>
      )}
      <ProductDetailsTabBox value="keywords">
        <KeywordsTab showReasoning={showReasoning} />
      </ProductDetailsTabBox>
      <ProductDetailsTabBox value="thema">
        <CodesTab />
      </ProductDetailsTabBox>
      <ProductDetailsTabBox value="description">
        <BookDescriptionTab />
      </ProductDetailsTabBox>
      <ProductDetailsTabBox value="marketing">
        <MarketingSubtitlesPanel />
      </ProductDetailsTabBox>
      <ProductDetailsTabBox value="salesData">
        <SalesDataTab
          isExplainerOpen={isSalesExplainerOpen}
          onDismissExplainer={dismissSalesExplainer}
        />
      </ProductDetailsTabBox>
      {features.showAPlusTab && (
        <ProductDetailsTabBox value="aPlusContent">
          <APlusContentTab />
        </ProductDetailsTabBox>
      )}
      {user.isSuperAdmin && (
        <ProductDetailsTabBox value="rawData">
          <RawDataTab />
        </ProductDetailsTabBox>
      )}
      <RegenerateKeywordsDialog
        onOpenChange={onRegenerateDialogChange}
        isOpen={isRegenerateDialogOpen}
      />
    </Tabs>
  );
};
